import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { apiClient } from '../services/base';
import AuthLayout from '../components/AuthLayout';
import AuthLogo from '../components/AuthLogo';
import { plumFieldSx, darkButtonSx, cancelLinkSx, PLUM } from '../components/authStyles';

// Google's Inter tracks a hair wider than Figma's; -0.01em reproduces the
// Figma line breaks at the 360px form width.
const infoTextSx = {
  fontSize: '16px',
  lineHeight: 1.5,
  letterSpacing: '-0.01em',
  color: PLUM,
  textAlign: 'center' as const,
  // Even out line lengths so centered copy never leaves a one-word last line.
  textWrap: 'balance' as const,
};

const infoLinkSx = {
  color: PLUM,
  fontWeight: 700,
  textDecoration: 'none',
  whiteSpace: 'nowrap' as const,
  '&:hover': { textDecoration: 'underline' },
};

const NewMember: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    if (!name.trim() || !email.trim()) {
      setError('Please enter at least your name and email address.');
      setLoading(false);
      return;
    }

    try {
      await apiClient.post('/auth/new-member', {
        name: name.trim(),
        email: email.trim(),
        company_name: companyName.trim(),
        country: country.trim(),
      });
      setSuccess(true);
    } catch (err: any) {
      let errorMessage = 'Something went wrong. Please try again.';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mb: { xs: 8, md: 9 } }}>
          <AuthLogo />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: '-0.02em',
            color: PLUM,
            textAlign: 'center',
            mb: 4,
          }}
        >
          Want to join the most advanced concrete thermal management system?
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
              Thank you! We will be in contact with you as soon as possible.
            </Alert>
            <Typography component={RouterLink} to="/login" sx={cancelLinkSx}>
              Back to Login
            </Typography>
          </>
        ) : (
          <>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
              <TextField
                fullWidth
                placeholder="Enter Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
                autoFocus
                autoComplete="name"
                sx={{ ...plumFieldSx, mb: 2.5 }}
              />
              <TextField
                fullWidth
                placeholder="Enter Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="email"
                sx={{ ...plumFieldSx, mb: 2.5 }}
              />
              <TextField
                fullWidth
                placeholder="Enter Company Name"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={loading}
                autoComplete="organization"
                sx={{ ...plumFieldSx, mb: 2.5 }}
              />
              <TextField
                fullWidth
                placeholder="Enter Country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={loading}
                autoComplete="country-name"
                sx={{ ...plumFieldSx, mb: 3 }}
              />

              <Typography sx={{ ...infoTextSx, mb: 2 }}>
                We will do our best to get in contact with you as soon as possible. Have you seen
                our website?{' '}
                <Box
                  component="a"
                  href="https://www.conxedge.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={infoLinkSx}
                >
                  www.ConXedge.com
                </Box>
              </Typography>
              <Typography sx={{ ...infoTextSx, mb: 3 }}>
                If you would like to, you can send an email to{' '}
                <Box component="a" href="mailto:sales.info@hdtechx.com" sx={infoLinkSx}>
                  sales.info@hdtechx.com
                </Box>
              </Typography>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                disableElevation
                sx={{ ...darkButtonSx, mb: 4 }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Send Details'}
              </Button>
            </Box>

            <Typography component={RouterLink} to="/login" sx={cancelLinkSx}>
              Cancel
            </Typography>
          </>
        )}
      </Box>
    </AuthLayout>
  );
};

export default NewMember;
